// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { orderTotalSummary } from "@bg/App/SharedStyles/GeneralStyles";
import { parseBoolean } from "@bg/XDL/Utilities";
import siteMessage from "@insite/client-framework/SiteMessage";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

export interface OrderDetailsTotalStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    subtotalLabelGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValueGridItem?: GridItemProps;
    subtotalValue?: TypographyProps;
    discountsLabelGridItem?: GridItemProps;
    discountsLabel?: TypographyProps;
    discountsValueGridItem?: GridItemProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabelGridItem?: GridItemProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValueGridItem?: GridItemProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabelGridItem?: GridItemProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValueGridItem?: GridItemProps;
    otherChargesValue?: TypographyProps;
    taxLabelGridItem?: GridItemProps;
    taxLabel?: TypographyProps;
    taxValueGridItem?: GridItemProps;
    taxValue?: TypographyProps;
    totalLabelGridItem?: GridItemProps;
    totalLabel?: TypographyProps;
    totalGridItem?: GridItemProps;
    totalValue?: TypographyProps;
    totalWrapper?: GridContainerProps;
    // XNG-Changes
    taxMessageGridItem?: GridItemProps;
    taxMessage?: TypographyProps;
}

export const totalStyles: OrderDetailsTotalStyles = {
    container: {
        gap: 5,
        css: css`
            @media print {
                &,
                * {
                    break-inside: avoid;
                    page-break-inside: avoid;
                }
            }
        `,
    },
    titleGridItem: {
        css: css`
            padding: 14px 0px;
            border-top: 2px solid ${getColor("common.ui3")};
            border-bottom: 2px solid ${getColor("common.ui3")};
        `,
    },
    subtotalLabelGridItem: {
        width: 6,
        css: css`
            padding-left: 0px;
        `,
    },
    subtotalValueGridItem: { width: 6 },
    subtotalLabel: {
        size: 15,
        color: "text",
        lineHeight: "22px",
        weight: "bold",
        css: css`
            margin-top: 20px;
        `,
    },
    subtotalValue: {
        size: 15,
        color: "text",
        lineHeight: "22px",
        weight: "bold",
        css: css`
            margin-top: 20px;
            margin-left: auto;
        `,
    },
    discountsLabelGridItem: {
        width: 6,
        css: css`
            padding-left: 0px;
        `,
    },
    discountsValueGridItem: { width: 6 },
    discountsValue: {
        css: css`
            margin-left: auto;
            color: ${getColor("common.brand3")};
        `,
    },
    shippingAndHandlingLabelGridItem: {
        width: 6,
        css: css`
            padding-left: 0px;
        `,
    },
    shippingAndHandlingValueGridItem: { width: 6 },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesLabelGridItem: {
        width: 6,
        css: css`
            padding-left: 0px;
        `,
    },
    otherChargesValueGridItem: { width: 6 },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxLabelGridItem: {
        width: 6,
        css: css`
            padding-left: 0px;
        `,
    },
    taxValueGridItem: { width: 6 },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },

    totalLabel: { size: 18, color: "text", lineHeight: "22px", weight: "bold" },
    totalGridItem: {
        width: 6,
        css: css`
            font-size: 22px;
            font-weight: bold;
            letter-spacing: 0.55px;
            line-height: 27px;
        `,
    },
    totalValue: {
        size: 18,
        color: "text",
        lineHeight: "22px",
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
    totalWrapper: {
        gap: 10,
        css: css`
            margin-top: 20px;
            padding: 20px 0;
            border-top: 2px solid ${getColor("common.ui3")};
        `,
    },
};

const styles = totalStyles;

const OrderDetailsTotal: FC = () => {
    const { value: order } = useContext(OrderStateContext);

    if (!order) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleGridItem} width={12}>
                <Typography {...orderTotalSummary.titleText}>{translate("Order Summary")}</Typography>
            </GridItem>
            <GridItem {...styles.subtotalLabelGridItem}>
                <Typography {...styles.subtotalLabel}>{translate("Subtotal")}</Typography>
            </GridItem>
            <GridItem {...styles.subtotalValueGridItem}>
                <Typography {...styles.subtotalValue}>{order.orderSubTotalDisplay}</Typography>
            </GridItem>
            {order.shippingCharges + order.handlingCharges > 0 && (
                <>
                    <GridItem {...styles.shippingAndHandlingLabelGridItem}>
                        <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}</Typography>
                    </GridItem>
                    <GridItem {...styles.shippingAndHandlingValueGridItem}>
                        <Typography {...styles.shippingAndHandlingValue}>{order.shippingAndHandlingDisplay}</Typography>
                    </GridItem>
                </>
            )}
            {(!order.orderHistoryTaxes || order.orderHistoryTaxes.length === 0) && (
                <>
                    <GridItem {...styles.taxLabelGridItem}>
                        <Typography {...styles.taxLabel}>{translate("Tax")}</Typography>
                    </GridItem>
                    <GridItem {...styles.taxValueGridItem}>
                        <Typography {...styles.taxValue} data-test-selector="orderDetails_totalTaxDisplay">
                            {order.totalTaxDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {order.orderHistoryTaxes &&
                order.orderHistoryTaxes.map(tax => (
                    <>
                        <GridItem {...styles.taxLabelGridItem}>
                            <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                        </GridItem>
                        <GridItem {...styles.taxValueGridItem}>
                            <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                        </GridItem>
                    </>
                ))}
            {/* XNG-Changes BG-2752: Add message for estimated tax amount on non-credit card orders */}
            {(order.properties?.showTaxMessage && parseBoolean(order.properties.showTaxMessage)) && (
                <GridItem {...styles.taxMessageGridItem}>
                    <Typography {...styles.taxMessage}>{siteMessage("Custom_Estimated_Tax_Amount_Message")}</Typography>
                </GridItem>
            )}
            {order.discountAmount > 0 && (
                <>
                    <GridItem {...styles.discountsLabelGridItem}>
                        <Typography {...styles.discountsLabel}>{translate("Promotions")}</Typography>
                    </GridItem>
                    <GridItem {...styles.discountsValueGridItem}>
                        <Typography {...styles.discountsValue}>{order.orderDiscountAmountDisplay}</Typography>
                    </GridItem>
                </>
            )}
            <GridContainer {...styles.totalWrapper}>
                <GridItem {...styles.totalGridItem}>
                    <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                </GridItem>
                <GridItem {...styles.totalGridItem}>
                    <Typography {...styles.totalValue} data-test-selector="orderDetail_orderGrandTotalDisplay">
                        {order.orderGrandTotalDisplay}
                    </Typography>
                </GridItem>
            </GridContainer>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsTotal,
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
